import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Spinner, Alert, Button } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import CommonDataTable from '../../common/DataTable';
import { getQuizeColumns } from '../../Components/Common/columns/Quiz';
import { bulkDeleteQuize, deleteQuize, fetchQuizeWithPage } from '../../slices/thunks';
import { useNavigate } from "react-router-dom";
import { setFilterParams } from '../../slices/quize/reducer';

const QuizeList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { quizzes, loading, error, filterParams } = useSelector(
    (state) => state.QuizeData || { 
      quizzes: { data: [] }, 
      filterParams: { currentPage: 1, perPage: 20 } 
    }
  );

  // Extract the nested data structure
  const quizeList = quizzes?.data?.data?.data || [];
  const totalCount = quizzes?.data?.data?.totalCount || 0;
  const currentPage = quizzes?.data?.data?.currentPage || 1;
  const totalPages = quizzes?.data?.data?.totalPages || 1;

  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmOpenForId, setConfirmOpenForId] = useState();

  useEffect(() => {
    const params = filterParams || { currentPage: 1, perPage: 20 };
    dispatch(fetchQuizeWithPage(params));
  }, [dispatch, filterParams]);

  const handleDelete = useCallback(async (quizeId) => {
    try {
      await dispatch(deleteQuize({ id: quizeId }));
      dispatch(fetchQuizeWithPage(filterParams));
    } catch (error) {
      console.error('Error deleting quize:', error);
    }
  }, [dispatch, filterParams]);

  const handleBulkDelete = useCallback(async () => {
    const quizeIds = selectedRows.map((row) => row.id);
    if (quizeIds.length > 0) {
      try {
        await dispatch(bulkDeleteQuize({ quizeIds }));
        dispatch(fetchQuizeWithPage(filterParams));
        setSelectedRows([]);
      } catch (error) {
        console.error('Error in bulk delete:', error);
      }
    }
  }, [dispatch, selectedRows, filterParams]);

  const fetchData = useCallback((newFilterParams) => {
    // Update filter params in Redux state
    dispatch(setFilterParams(newFilterParams));
    // Fetch new data with updated params
    dispatch(fetchQuizeWithPage(newFilterParams));
  }, [dispatch]);

  const columns = getQuizeColumns({
    onDeleteClick: handleDelete,
    confirmOpenForId,
    setConfirmOpenForId,
  });

  const handleAddButtonClick = () => {
    navigate('/quiz/add-form');
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="quizzes" pageTitle="quizzes" />
        </Container>
      </div>
      <div className="table-container">
        {loading && (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Loading quizes...</p>
          </div>
        )}

        {error && (
          <Alert color="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && quizeList.length === 0 && (
          <div className="text-center my-4">
            <h4>No quizes available</h4>
            <p>Click the Add button to create a new quiz</p>
          </div>
        )}

        {!loading && !error && (
          <CommonDataTable
            from="quize"
            data={quizeList}
            totalRows={totalCount}
            loading={loading}
            showAddButton={true}
            addOnClickMethod={handleAddButtonClick}
            columns={columns}
            checkboxEnabled={false}
            bulkDelete={handleBulkDelete}
            selectedRows={selectedRows}
            bulkSelected={(rows) => setSelectedRows(rows)}
            searchEnable={false}
            filterParams={{ ...filterParams, currentPage }}
            fetchData={fetchData}
            showExportButton={false}
            exportFileName="quize"
            currentPage={currentPage}
            totalPages={totalPages}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default QuizeList;
